import React, { useState, useEffect } from 'react';

// RESPONSIVE TEXT BLOCK
export const TextBlock = ({ content, size = 'normal', align = 'left' }) => (
  <div style={{ 
    marginBottom: 'clamp(12px, 2.5vw, 20px)',
    textAlign: align,
    maxWidth: '700px',
    marginLeft: align === 'center' ? 'auto' : '0',
    marginRight: align === 'center' ? 'auto' : '0'
  }}>
    <p style={{ 
      fontSize: size === 'large' ? 'clamp(1.1rem, 1.8vw, 1.2rem)' : 
                size === 'small' ? 'clamp(0.875rem, 1.3vw, 0.95rem)' : 
                'clamp(1rem, 1.5vw, 1.05rem)',
      color: 'var(--text-secondary)',
      lineHeight: '1.6',
      margin: 0,
      fontWeight: '400'
    }}>
      {content}
    </p>
  </div>
);

// ELEGANT TITLE BLOCK
export const TitleBlock = ({ text, size = 'h2', accent = false }) => (
  <div style={{ 
    marginBottom: 'clamp(16px, 3vw, 24px)',
    marginTop: size === 'h1' ? 'clamp(24px, 4vw, 40px)' : 'clamp(20px, 3.5vw, 32px)'
  }}>
    {size === 'h1' && (
      <h1 style={{ 
        fontSize: 'clamp(1.8rem, 4vw, 2.4rem)',
        fontWeight: '700',
        color: accent ? 'var(--text-accent)' : 'var(--text-primary)',
        lineHeight: '1.2',
        letterSpacing: '-0.01em',
        margin: 0
      }}>{text}</h1>
    )}
    {size === 'h2' && (
      <h2 style={{ 
        fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
        fontWeight: '600',
        color: 'var(--text-primary)',
        lineHeight: '1.3',
        letterSpacing: '-0.005em',
        margin: 0
      }}>{text}</h2>
    )}
    {size === 'h3' && (
      <h3 style={{ 
        fontSize: 'clamp(1.2rem, 2.5vw, 1.4rem)',
        fontWeight: '500',
        color: 'var(--text-primary)',
        lineHeight: '1.4',
        margin: 0
      }}>{text}</h3>
    )}
  </div>
);

// RESPONSIVE VIDEO BLOCK
export const VideoBlock = ({ url, title, maxWidth = '100%' }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const getEmbedUrl = (url) => {
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?h=${vimeoMatch[2]}&badge=0&autopause=0&player_id=0&app_id=58479`;
    }
    return url;
  };

  // Check if it's a direct video file (Cloudinary MP4)
  const isDirectVideo = url.includes('cloudinary.com') && url.includes('.mp4');

  return (
    <div style={{ 
      maxWidth,
      marginBottom: 'clamp(20px, 4vw, 32px)',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
<div style={{ 
        position: 'relative', 
        width: '100%',
        minHeight: isMobile ? '300px' : '500px',
        borderRadius: 'clamp(6px, 1vw, 10px)',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        background: 'var(--bg-secondary)',
        overflow: 'hidden'
      }}>
        {isDirectVideo ? (
          <video
            src={getEmbedUrl(url)}
            controls
            style={{
              width: '100%',
              height: '100%',
              minHeight: isMobile ? '300px' : '500px',
              objectFit: 'contain'
            }}
          />
        ) : (
          <iframe
            src={getEmbedUrl(url)}
            title="Embedded video"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
      {title && (
        <p style={{ 
          fontSize: 'clamp(0.85rem, 1.3vw, 0.9rem)',
          color: 'var(--text-secondary)', 
          marginTop: 'clamp(8px, 1.5vw, 12px)',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          {title}
        </p>
      )}
    </div>
  );
};



// AESTHETIC IMAGE BLOCK
export const ImageBlock = ({ src, alt, caption, maxWidth = '100%', rounded = true }) => (
  <div style={{ 
    maxWidth,
    marginBottom: 'clamp(20px, 4vw, 32px)',
    marginLeft: 'auto',
    marginRight: 'auto'
  }}>
    <div style={{
      overflow: 'hidden',
      borderRadius: rounded ? 'clamp(6px, 1vw, 10px)' : '0',
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
      border: '1px solid var(--border-color)',
      background: 'var(--bg-secondary)'
    }}>
      <img 
        src={src} 
        alt={alt} 
        style={{ 
          width: '100%',
          display: 'block',
          height: 'auto'
        }} 
      />
    </div>
    {caption && (
      <p style={{ 
        fontSize: 'clamp(0.8rem, 1.2vw, 0.85rem)',
        color: 'var(--text-secondary)', 
        marginTop: 'clamp(6px, 1vw, 8px)',
        textAlign: 'center',
        fontStyle: 'italic',
        padding: '0 12px'
      }}>
        {caption}
      </p>
    )}
  </div>
);

// SMART RESPONSIVE SIDE BY SIDE
export const SideBySideBlock = ({ left, right, split = '50/50', mobileStack = true }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [leftSize, rightSize] = split.split('/');

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <div 
      className="responsive-sidebyside"
      style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile && mobileStack ? '1fr' : `${leftSize}fr ${rightSize}fr`,
        gap: 'clamp(20px, 4vw, 32px)',
        marginBottom: 'clamp(24px, 4vw, 40px)',
        alignItems: 'start'
      }}
    >
      <div className="sidebyside-left">{left}</div>
      <div className="sidebyside-right">{right}</div>
    </div>
  );
};

// BEAUTIFUL GALLERY BLOCK
export const GalleryBlock = ({ images, columns = 3 }) => (
  <div style={{ 
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${
      columns === 2 ? '240px' : 
      columns === 3 ? '180px' : 
      columns === 4 ? '140px' : '220px'
    }, 1fr))`,
    gap: 'clamp(12px, 1.8vw, 18px)',
    marginBottom: 'clamp(24px, 4vw, 40px)'
  }}>
    {images.map((img, i) => (
      <div key={i} style={{ 
        borderRadius: 'clamp(6px, 1vw, 8px)',
        overflow: 'hidden',
        boxShadow: '0 1px 8px rgba(0, 0, 0, 0.06)',
        border: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 8px rgba(0, 0, 0, 0.06)';
      }}>
        <img 
          src={img.src} 
          alt={img.alt || ''} 
          style={{ 
            width: '100%', 
            display: 'block',
            aspectRatio: '16/10',
            objectFit: 'cover'
          }}
        />
        {img.caption && (
          <p style={{ 
            padding: 'clamp(6px, 1vw, 8px)', 
            fontSize: 'clamp(0.75rem, 1.1vw, 0.8rem)',
            color: 'var(--text-secondary)',
            textAlign: 'center',
            margin: 0
          }}>
            {img.caption}
          </p>
        )}
      </div>
    ))}
  </div>
);

// ELEGANT SPACER
export const SpacerBlock = ({ size = 'medium' }) => {
  const heights = {
    small: 'clamp(12px, 2vw, 16px)',
    medium: 'clamp(20px, 3.5vw, 28px)',
    large: 'clamp(32px, 5vw, 48px)'
  };
  
  return <div style={{ height: heights[size] || heights.medium }} />;
};

// MODERN BUTTON BLOCK
export const ButtonBlock = ({ text, url, style = 'primary', size = 'medium' }) => {
  const sizes = {
    small: { padding: '10px 22px', fontSize: 'clamp(0.85rem, 1.3vw, 0.9rem)' },
    medium: { padding: '13px 28px', fontSize: 'clamp(0.95rem, 1.5vw, 1rem)' },
    large: { padding: '16px 36px', fontSize: 'clamp(1rem, 1.6vw, 1.1rem)' }
  };

  const buttonStyles = {
    primary: {
      bg: 'var(--text-primary, #111)',
      color: 'var(--bg-primary, #fff)',
      border: 'var(--text-primary, #111)',
      hoverBg: 'transparent',
      hoverColor: 'var(--text-primary, #111)'
    },
    secondary: {
      bg: 'transparent',
      color: 'var(--text-primary, #111)',
      border: 'var(--text-secondary, #666)',
      hoverBg: 'var(--text-primary, #111)',
      hoverColor: 'var(--bg-primary, #fff)'
    },
    tonelab: {
      bg: 'rgb(39, 11, 14)',
      color: '#fff',
      border: 'rgb(80, 30, 35)',
      hoverBg: 'transparent',
      hoverColor: 'var(--text-primary, #fff)'
    },
    foundry: {
      bg: '#1a1a2e',
      color: '#fff',
      border: '#2c3e50',
      hoverBg: 'transparent',
      hoverColor: 'var(--text-primary, #fff)'
    },
    moodify: {
      bg: '#00b894',
      color: '#fff',
      border: '#00b894',
      hoverBg: 'transparent',
      hoverColor: '#00b894'
    },
    sportship: {
      bg: '#e17055',
      color: '#fff',
      border: '#e17055',
      hoverBg: 'transparent',
      hoverColor: '#e17055'
    },
    najdar: {
      bg: '#2d3436',
      color: '#fff',
      border: '#636e72',
      hoverBg: 'transparent',
      hoverColor: 'var(--text-primary, #fff)'
    },
    chibidash: {
      bg: '#00cec9',
      color: '#111',
      border: '#00cec9',
      hoverBg: 'transparent',
      hoverColor: '#00cec9'
    }
  };

  const s = buttonStyles[style] || buttonStyles.primary;

  return (
    <div style={{
      marginBottom: 'clamp(20px, 3vw, 28px)',
      textAlign: 'center'
    }}>
      <a
        href={url}
        target={url.startsWith('http') ? '_blank' : '_self'}
        rel={url.startsWith('http') ? 'noopener noreferrer' : ''}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: sizes[size].padding,
          borderRadius: '12px',
          textDecoration: 'none',
          background: s.bg,
          color: s.color,
          border: `1.5px solid ${s.border}`,
          fontSize: sizes[size].fontSize,
          fontWeight: '500',
          letterSpacing: '0.01em',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = s.hoverBg;
          e.currentTarget.style.color = s.hoverColor;
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = s.bg;
          e.currentTarget.style.color = s.color;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {text}
        <span style={{ fontSize: '1.1em', transition: 'transform 0.2s' }}>&#8599;</span>
      </a>
    </div>
  );
};

// 📝 HOW TO USE IN YOUR RICH CONTENT:
// { type: 'button', text: 'Visit Tonelab', style: 'tonelab' }
// { type: 'button', text: 'View Foundry', style: 'foundry' }  
// { type: 'button', text: 'See Moodify', style: 'moodify' }
// { type: 'button', text: 'Learn More', style: 'primary' }

// CONTAINER BLOCK - For sections with background
export const ContainerBlock = ({ children, background = false, narrow = false }) => (
  <div style={{
    background: background ? 'var(--bg-secondary)' : 'transparent',
    // a plain (no background) container adds no inset and is never centred, so
    // what sits in it lines up with the rest of the page's left edge
    padding: background ? 'clamp(16px, 2.5vw, 20px)' : '0',
    borderRadius: background ? 'clamp(6px, 1vw, 8px)' : '0',
    border: background ? '1px solid var(--border-color)' : 'none',
    marginBottom: 'clamp(20px, 3vw, 28px)',
    maxWidth: narrow ? '680px' : '100%',
    marginLeft: 0,
    marginRight: 0
  }}>
    {children}
  </div>
);